<template>
  <div class="page-container">
    <el-card class="page-content" shadow="never">
      <div class="title">{{ title }}</div>
      <filters
        :filters="filters"
        :export-loading="exportLoading"
        @search="search"
        @reset="reset"
        @export="download(title)"
      />

      <div class="summary" v-loading="summaryLoading">
        <div class="summary-item">
          <span>总领取金额：</span>
          <strong>{{ amount(summary.totalGrantAmount) }} USDT</strong>
        </div>
        <div class="summary-item">
          <span>总回收金额：</span>
          <strong>{{ amount(summary.totalRecycleAmount) }} USDT</strong>
        </div>
        <div class="summary-item">
          <span>总抵扣金额：</span>
          <strong>{{ amount(summary.totalDeductAmount) }} USDT</strong>
        </div>
      </div>

      <el-table :data="rows" size="mini" v-loading="loading" height="calc(100vh - 350px)">
        <el-table-column prop="userUid" label="用户UID" min-width="110" />
        <el-table-column prop="userTypeDesc" label="用户类型" min-width="100" />
        <el-table-column prop="partnerUid" label="合伙人UID" min-width="110">
          <template slot-scope="scope">{{ display(scope.row.partnerUid) }}</template>
        </el-table-column>
        <el-table-column prop="statisticsDate" label="统计日期" min-width="110" />
        <el-table-column label="卡券ID" min-width="190">
          <template slot-scope="scope">
            <coupon-id-list
              :items="scope.row.couponIds"
              :summary-items="couponSummaryItems(scope.row)"
              :dialog-title="couponIdLabel"
              :item-label="couponIdLabel"
              empty-text="--"
              show-count
            />
          </template>
        </el-table-column>
        <el-table-column prop="grantAmount" label="领取金额" min-width="110">
          <template slot-scope="scope">{{ amount(scope.row.grantAmount) }}</template>
        </el-table-column>
        <el-table-column prop="recycleAmount" label="回收金额" min-width="110">
          <template slot-scope="scope">{{ amount(scope.row.recycleAmount) }}</template>
        </el-table-column>
        <el-table-column prop="deductAmount" label="抵扣金额" min-width="110">
          <template slot-scope="scope">
            <el-button type="text" @click="openDetail(scope.row)">
              {{ amount(scope.row.deductAmount) }}
            </el-button>
          </template>
        </el-table-column>
        <el-table-column prop="deductRate" label="抵扣比例" min-width="100">
          <template slot-scope="scope">
            {{ display(scope.row.deductRate) }}{{ scope.row.deductRate == null ? '' : '%' }}
          </template>
        </el-table-column>
        <el-table-column prop="coin" label="币种" min-width="80" />
        <el-table-column prop="remainingAmount" label="余额" min-width="110">
          <template slot-scope="scope">{{ amount(scope.row.remainingAmount) }}</template>
        </el-table-column>
        <el-table-column label="回收状态" min-width="110">
          <template slot="header">
            <el-tooltip content="金额为统计日期数据，状态展示该抵扣券周期的当前状态" placement="top">
              <span>回收状态 <i class="el-icon-info" /></span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            <manual-operation :row="scope.row" deduct display="status" />
          </template>
        </el-table-column>
        <el-table-column v-if="canRecycle" label="操作" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button v-if="scope.row.canRecycle && !scope.row.manuallyRecycled" type="text"
              :loading="recyclingCycle === scope.row.cycleId" :disabled="recyclingCycle !== null"
              @click="recycle(scope.row)">回收</el-button>
            <span v-else>--</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="pag">
        <pagination
          :size="pagination.size"
          :total="total"
          :page="pagination.page"
          @pageChanged="pageChanged"
        />
      </div>

      <el-dialog
        :title="detailDialogTitle"
        :visible.sync="detailVisible"
        width="780px"
        append-to-body
        custom-class="deduct-detail-dialog"
      >
        <div v-loading="detailLoading" class="deduct-detail-content">
          <div class="deduct-detail-summary">
            <div class="deduct-detail-summary-item">
              <span>用户UID：</span>
              <strong>{{ display(detailRow.userUid) }}</strong>
            </div>
            <div class="deduct-detail-summary-item">
              <span>合伙人UID：</span>
              <strong>{{ display(detailRow.partnerUid) }}</strong>
            </div>
            <div class="deduct-detail-summary-item">
              <span>统计日期：</span>
              <strong>{{ display(detailRow.statisticsDate) }}</strong>
            </div>
            <div class="deduct-detail-summary-item deduct-detail-total">
              <span>{{ detailAmountLabel }}：</span>
              <strong>{{ amountWithUnit(detailRow.deductAmount) }}</strong>
            </div>
            <div class="deduct-detail-summary-item">
              <span>抵扣比例：</span>
              <strong>{{ deductRate(detailRow.deductRate) }}</strong>
            </div>
          </div>

          <div class="deduct-detail-table-header">
            <span>抵扣时间</span>
            <span>业务类型</span>
            <span>仓位ID</span>
            <span>{{ originalAmountLabel }}</span>
            <span>{{ detailAmountLabel }}</span>
          </div>
          <div class="deduct-detail-table-body">
            <div
              v-for="(item, index) in details"
              :key="item.consumeOrderId || `${item.sourceId}-${index}`"
              class="deduct-detail-table-row"
            >
              <span>{{ time(item.confirmedTime) }}</span>
              <span>{{ businessType(item.sourceType) }}</span>
              <span>{{ display(item.sourceId) }}</span>
              <span>{{ originalAmount(item.baseAmount, item.coin) }}</span>
              <span class="deduct-detail-amount">
                {{ amountWithUnit(item.deductAmount, item.coin) }}
              </span>
            </div>
            <div v-if="!detailLoading && !details.length" class="deduct-detail-empty">
              暂无数据
            </div>
          </div>
        </div>

        <div slot="footer" class="deduct-detail-footer">
          <el-button type="primary" @click="detailVisible = false">关闭</el-button>
        </div>
      </el-dialog>
    </el-card>
  </div>
</template>

<script>
import Decimal from 'decimal.js'
import Pagination from 'components/pagination'
import CouponIdList from '../components/CouponIdList'
import ManualOperation from '../components/ManualOperation'
import Filters from './Filters'
import mixin, { createFilters } from './mixin'
import api from './api'
import { hasPerm } from 'common/permission'

export default {
  name: 'WelfareDeductStatistic',
  components: { Pagination, CouponIdList, Filters, ManualOperation },
  mixins: [mixin],
  props: {
    kind: { type: String, required: true },
    title: { type: String, required: true },
  },
  data() {
    return {
      summary: {},
      summaryLoading: false,
      detailVisible: false,
      detailLoading: false,
      details: [],
      detailRow: {},
      recyclingCycle: null,
    }
  },
  computed: {
    canRecycle() {
      const family = this.kind === 'fee' ? 'fee_deduct' : 'pnl_deduct'
      return hasPerm(this.$store.state.userInfo || {}, `tenant:welfare_${family}_stats:recycle`)
    },
    detailDialogTitle() {
      return this.kind === 'fee' ? '手续费抵扣明细' : '盈亏抵扣明细'
    },
    detailAmountLabel() {
      return this.kind === 'fee' ? '手续费抵扣金额' : '盈亏抵扣金额'
    },
    originalAmountLabel() {
      return this.kind === 'fee' ? '原手续费' : '原始金额'
    },
    couponIdLabel() {
      return `${this.title.replace('数据统计', '')}ID`
    },
  },
  watch: {
    kind: {
      immediate: true,
      handler() {
        this.filters = createFilters()
        this.pagination = { page: 1, size: 10 }
        this.rows = []
        this.total = 0
        this.summary = {}
        this.detailVisible = false
        this.fetchAll()
      },
    },
  },
  methods: {
    async recycle(row) {
      if (!this.canRecycle || this.recyclingCycle !== null) return
      try {
        await this.$confirm('确认对该记录执行手动回收吗？操作后不可撤销，请谨慎操作。', '提示', {
          confirmButtonText: '确认', cancelButtonText: '取消', type: 'warning',
        })
      } catch (_) {
        return
      }
      this.recyclingCycle = row.cycleId
      try {
        const result = await api.recycle(this.kind, {
          userUid: row.userUid, coin: row.coin, cycleId: row.cycleId, statisticsDate: row.statisticsDate,
        })
        if (!result || result.code !== 0 || result.data !== true) throw new Error((result && result.msg) || '回收结果未确认，请刷新列表查看')
        this.$message.success('回收成功')
      } catch (error) {
        this.$message.error((error && error.message) || '回收结果未确认，请刷新列表查看后重试')
      } finally {
        this.recyclingCycle = null
        this.fetchAll()
      }
    },
    couponSummaryItems(row) {
      return [
        { label: '用户UID', value: row.userUid },
        { label: '合伙人UID', value: row.partnerUid },
        { label: '统计日期', value: row.statisticsDate },
      ]
    },
    amountWithUnit(value, coin = this.detailRow.coin) {
      if (value === null || value === undefined || value === '') return '-'
      try {
        const amount = new Decimal(value)
          .abs()
          .toDecimalPlaces(4, Decimal.ROUND_DOWN)
          .toFixed(4)
        return `${amount}${coin ? ` ${coin}` : ''}`
      } catch (error) {
        return value
      }
    },
    originalAmount(value, coin = this.detailRow.coin) {
      if (value === null || value === undefined || value === '') return '-'
      try {
        const amount = new Decimal(value)
          .abs()
          .toDecimalPlaces(4, Decimal.ROUND_DOWN)
          .toFixed(4)
        return `-${amount}${coin ? ` ${coin}` : ''}`
      } catch (error) {
        return value
      }
    },
    deductRate(value) {
      return value === null || value === undefined || value === '' ? '-' : `${value}%`
    },
    businessType(sourceType) {
      const typeMap = {
        FEE_SETTLEMENT: '手续费',
        PNL_SETTLEMENT: '盈亏',
      }
      return typeMap[sourceType] || '-'
    },
    fetchAll() {
      if (!this.valid()) return
      this.fetchList()
      this.summaryLoading = true
      api
        .summary(this.kind, this.query(false))
        .then((res) => {
          if (!res || res.code !== 0) throw new Error((res && res.msg) || '汇总查询失败')
          this.summary = res.data || {}
        })
        .catch((error) => {
          this.summary = {}
          this.$message.error(error.message)
        })
        .finally(() => {
          this.summaryLoading = false
        })
    },
    openDetail(row) {
      this.detailRow = row
      this.detailVisible = true
      this.detailLoading = true
      this.details = []
      api
        .detail(this.kind, {
          userUid: row.userUid,
          cycleId: row.cycleId,
          statisticsDate: row.statisticsDate,
        })
        .then((res) => {
          if (!res || res.code !== 0) throw new Error((res && res.msg) || '明细查询失败')
          this.details = res.data || []
        })
        .catch((error) => {
          this.$message.error(error.message)
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
  },
}
</script>

<style lang="less" scoped>
.title {
  margin-bottom: 18px;
  color: #409eff;
  font-size: 16px;
}

.summary {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 36px;
  min-height: 25px;
  margin-bottom: 16px;
  font-size: 13px;
}

.summary-item {
  min-width: 220px;
}

.summary-item strong {
  color: #e6a23c;
  font-weight: normal;
}

.pag {
  display: flex;
  justify-content: center;
}

.deduct-detail-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  row-gap: 10px;
  padding: 14px 18px;
  margin-bottom: 20px;
  color: #7d8799;
  background: #f6f8fb;
  border-radius: 6px;
}

.deduct-detail-summary-item {
  display: flex;
  align-items: center;
  min-width: 0;
  line-height: 20px;
}

.deduct-detail-summary-item strong {
  color: #647084;
  font-weight: normal;
}

.deduct-detail-total strong {
  color: #ff8a4c;
}

.deduct-detail-table-header,
.deduct-detail-table-row {
  display: grid;
  grid-template-columns: 150px 95px minmax(180px, 1fr) 110px 120px;
  align-items: center;
  min-height: 48px;
  padding: 0 10px;
  color: #7d8799;
}

.deduct-detail-table-header {
  border-bottom: 1px solid #e8edf3;
}

.deduct-detail-table-body {
  max-height: 336px;
  padding-top: 12px;
  overflow-y: auto;
}

.deduct-detail-table-row {
  border-radius: 6px;
}

.deduct-detail-table-row:nth-child(odd) {
  background: #f6f8fb;
}

.deduct-detail-table-row span {
  min-width: 0;
  word-break: break-all;
}

.deduct-detail-amount {
  color: #4f8cff;
}

.deduct-detail-empty {
  padding: 42px 0;
  color: #909399;
  text-align: center;
}

.deduct-detail-footer {
  text-align: center;
}

::v-deep .deduct-detail-dialog .el-dialog__header {
  padding: 24px 20px 12px;
  text-align: center;
}

::v-deep .deduct-detail-dialog .el-dialog__title {
  color: #303849;
  font-size: 18px;
}

::v-deep .deduct-detail-dialog .el-dialog__body {
  padding: 14px 38px 0;
}

::v-deep .deduct-detail-dialog .el-dialog__footer {
  padding: 12px 20px 22px;
}

::v-deep .deduct-detail-dialog .deduct-detail-footer .el-button {
  min-width: 96px;
}
</style>
